import { MfaFactor, experience } from '@logto/schemas';
import { lazy, Suspense } from 'react';
import { Route, Routes, BrowserRouter } from 'react-router-dom';

import { LoadingIconWithContainer } from '@/shared/components/LoadingLayer';
import { handleSearchParametersData } from '@/shared/utils/search-parameters';

import AppLayout from './Layout/AppLayout';
import AppBoundary from './Providers/AppBoundary';
import CaptchaContextProvider from './Providers/CaptchaContextProvider';
import LoadingLayerProvider from './Providers/LoadingLayerProvider';
import PageContextProvider from './Providers/PageContextProvider';
import SettingsProvider from './Providers/SettingsProvider';
import UserInteractionContextProvider from './Providers/UserInteractionContextProvider';
import DevelopmentTenantNotification from './containers/DevelopmentTenantNotification';

import ErrorPage from './pages/ErrorPage';
import SignIn from './pages/SignIn';
import { UserMfaFlow } from './types';
import 'overlayscrollbars/overlayscrollbars.css';
import './shared/scss/normalized.scss';
import './scss/overlayscrollbars.scss';

// Keep the login form eager; later authentication steps load on navigation.
const Register = lazy(() => import('./pages/Register'));
const Callback = lazy(() => import('./pages/Callback'));
const Consent = lazy(() => import('./pages/Consent'));
const Continue = lazy(() => import('./pages/Continue'));
const Device = lazy(() => import('./pages/Device'));
const DeviceSuccess = lazy(() => import('./pages/Device/Success'));
const DirectSignIn = lazy(() => import('./pages/DirectSignIn'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const IdentifierRegister = lazy(() => import('./pages/IdentifierRegister'));
const IdentifierSignIn = lazy(() => import('./pages/IdentifierSignIn'));
const MfaBinding = lazy(() => import('./pages/MfaBinding'));
const BackupCodeBinding = lazy(() => import('./pages/MfaBinding/BackupCodeBinding'));
const EmailMfaBinding = lazy(() => import('./pages/MfaBinding/EmailMfaBinding'));
const PhoneMfaBinding = lazy(() => import('./pages/MfaBinding/PhoneMfaBinding'));
const TotpBinding = lazy(() => import('./pages/MfaBinding/TotpBinding'));
const WebAuthnBinding = lazy(() => import('./pages/MfaBinding/WebAuthnBinding'));
const MfaOnboarding = lazy(() => import('./pages/MfaOnboarding'));
const MfaVerification = lazy(() => import('./pages/MfaVerification'));
const BackupCodeVerification = lazy(() => import('./pages/MfaVerification/BackupCodeVerification'));
const EmailVerificationCode = lazy(() => import('./pages/MfaVerification/EmailVerificationCode'));
const PhoneVerificationCode = lazy(() => import('./pages/MfaVerification/PhoneVerificationCode'));
const TotpVerification = lazy(() => import('./pages/MfaVerification/TotpVerification'));
const WebAuthnVerification = lazy(() => import('./pages/MfaVerification/WebAuthnVerification'));
const OneTimeToken = lazy(() => import('./pages/OneTimeToken'));
const OneTimeTokenErrorPage = lazy(() => import('./pages/OneTimeToken/Error'));
const PasskeySetup = lazy(() => import('./pages/PasskeySetup'));
const RegisterPassword = lazy(() => import('./pages/RegisterPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const ResetPasswordLanding = lazy(() => import('./pages/ResetPasswordLanding'));
const SignInPasskeyVerification = lazy(() => import('./pages/SignInPasskeyVerification'));
const SignInPassword = lazy(() => import('./pages/SignInPassword'));
const SignInVerificationMethods = lazy(() => import('./pages/SignInVerificationMethods'));
const SingleSignOnConnectors = lazy(() => import('./pages/SingleSignOnConnectors'));
const SingleSignOnEmail = lazy(() => import('./pages/SingleSignOnEmail'));
const SingleSignOnLanding = lazy(() => import('./pages/SingleSignOnLanding'));
const SocialLanding = lazy(() => import('./pages/SocialLanding'));
const SocialLinkAccount = lazy(() => import('./pages/SocialLinkAccount'));
const SocialSignInWebCallback = lazy(() => import('./pages/SocialSignInWebCallback'));
const Springboard = lazy(() => import('./pages/Springboard'));
const SwitchAccount = lazy(() => import('./pages/SwitchAccount'));
const VerificationCode = lazy(() => import('./pages/VerificationCode'));

handleSearchParametersData();

const App = () => {
  return (
    <BrowserRouter>
      <PageContextProvider>
        <SettingsProvider>
          <UserInteractionContextProvider>
            <CaptchaContextProvider>
              <DevelopmentTenantNotification />
              <AppBoundary>
                <Suspense fallback={<LoadingIconWithContainer />}>
                  <Routes>
                    <Route element={<LoadingLayerProvider />}>
                      <Route path="springboard" element={<Springboard />} />
                      <Route path="callback/:connectorId" element={<Callback />} />
                      <Route
                        path="callback/social/:connectorId"
                        element={<SocialSignInWebCallback />}
                      />
                      <Route path="direct/:method/:target?" element={<DirectSignIn />} />
                      <Route path={experience.routes.oneTimeToken} element={<OneTimeToken />} />
                      <Route element={<AppLayout />}>
                        <Route
                          path={`${experience.routes.oneTimeToken}/error`}
                          element={<OneTimeTokenErrorPage />}
                        />
                        <Route path={experience.routes.switchAccount} element={<SwitchAccount />} />
                        <Route
                          path="unknown-session"
                          element={<ErrorPage message="error.invalid_session" />}
                        />
                        <Route
                          path={experience.routes.accountSuspended}
                          element={
                            <ErrorPage
                              isNavbarHidden
                              title="error.account_suspended"
                              message="error.account_suspended_description"
                              primaryAction={{
                                title: 'description.back_to_sign_in',
                                to: `/${experience.routes.signIn}`,
                                replace: true,
                              }}
                            />
                          }
                        />

                        {/* Sign-in */}
                        <Route path={experience.routes.signIn}>
                          <Route index element={<SignIn />} />
                          <Route path="password" element={<SignInPassword />} />
                          <Route path="passkey" element={<SignInPasskeyVerification />} />
                          <Route
                            path="verification-methods"
                            element={<SignInVerificationMethods />}
                          />
                        </Route>

                        {/* Create passkey for sign-in */}
                        <Route path="create-passkey" element={<PasskeySetup />} />

                        {/* Register */}
                        <Route path={experience.routes.register}>
                          <Route index element={<Register />} />
                          <Route path="password" element={<RegisterPassword />} />
                        </Route>

                        {/* Forgot password */}
                        <Route path="forgot-password">
                          <Route index element={<ForgotPassword />} />
                          <Route path="reset" element={<ResetPassword />} />
                        </Route>

                        {/* Passwordless verification code */}
                        <Route path=":flow/verification-code" element={<VerificationCode />} />

                        {/* Mfa onboarding page. Prompt users to turn on 2-step verification. */}
                        <Route path="mfa-onboarding" element={<MfaOnboarding />} />

                        {/* Mfa binding */}
                        <Route path={UserMfaFlow.MfaBinding}>
                          <Route index element={<MfaBinding />} />
                          <Route path={MfaFactor.TOTP} element={<TotpBinding />} />
                          <Route path={MfaFactor.WebAuthn} element={<WebAuthnBinding />} />
                          <Route path={MfaFactor.BackupCode} element={<BackupCodeBinding />} />
                          <Route
                            path={MfaFactor.EmailVerificationCode}
                            element={<EmailMfaBinding />}
                          />
                          <Route
                            path={MfaFactor.PhoneVerificationCode}
                            element={<PhoneMfaBinding />}
                          />
                        </Route>

                        {/* Mfa verification */}
                        <Route path={UserMfaFlow.MfaVerification}>
                          <Route index element={<MfaVerification />} />
                          <Route path={MfaFactor.TOTP} element={<TotpVerification />} />
                          <Route path={MfaFactor.WebAuthn} element={<WebAuthnVerification />} />
                          <Route path={MfaFactor.BackupCode} element={<BackupCodeVerification />} />
                          <Route
                            path={MfaFactor.EmailVerificationCode}
                            element={<EmailVerificationCode />}
                          />
                          <Route
                            path={MfaFactor.PhoneVerificationCode}
                            element={<PhoneVerificationCode />}
                          />
                        </Route>

                        {/* Continue set up missing profile */}
                        <Route path="continue">
                          <Route path=":method" element={<Continue />} />
                        </Route>

                        {/* Social sign-in pages */}
                        <Route path="social">
                          <Route path="link/:connectorId" element={<SocialLinkAccount />} />
                          <Route path="landing/:connectorId" element={<SocialLanding />} />
                        </Route>

                        {/* Single sign-on */}
                        <Route path={experience.routes.sso}>
                          {/* Single sign-on first screen landing page */}
                          <Route index element={<SingleSignOnLanding />} />
                          <Route path="email" element={<SingleSignOnEmail />} />
                          <Route path="connectors" element={<SingleSignOnConnectors />} />
                        </Route>

                        {/* Consent */}
                        <Route path="consent" element={<Consent />} />

                        {/* Device flow */}
                        <Route path={experience.routes.device} element={<Device />} />
                        <Route
                          path={`${experience.routes.device}/success`}
                          element={<DeviceSuccess />}
                        />

                        {/*
                         * Identifier sign-in (first screen)
                         * The first screen which only display specific identifier-based sign-in methods to users
                         */}
                        <Route
                          path={experience.routes.identifierSignIn}
                          element={<IdentifierSignIn />}
                        />

                        {/*
                         * Identifier register (first screen)
                         * The first screen which only display specific identifier-based registration methods to users
                         */}
                        <Route
                          path={experience.routes.identifierRegister}
                          element={<IdentifierRegister />}
                        />

                        {/*
                         * Reset password (first screen)
                         * The first screen which allow users to directly access the password reset page
                         */}
                        <Route
                          path={experience.routes.resetPassword}
                          element={<ResetPasswordLanding />}
                        />
                        <Route path="*" element={<ErrorPage />} />
                      </Route>
                    </Route>
                  </Routes>
                </Suspense>
              </AppBoundary>
            </CaptchaContextProvider>
          </UserInteractionContextProvider>
        </SettingsProvider>
      </PageContextProvider>
    </BrowserRouter>
  );
};

export default App;
